// Enhanced StatusBar Component with improved accessibility
import React from 'react';
import { FiImage, FiFilm, FiInfo } from 'react-icons/fi';
import { formatFileSize } from '../utils/helpers';

/**
 * Accessible component to display file statistics and limits
 * - Improved screen reader information
 * - Enhanced semantic structure
 * - Better visual indicators for limits
 *
 * @param {Object} props - Component props
 * @param {number} props.imageCount - Number of image files
 * @param {number} props.videoCount - Number of video files
 * @param {number} props.totalSize - Total size of all files in bytes
 * @param {number} props.videoLimit - Maximum number of videos allowed
 * @param {string} props.variant - Display variant ('default' or 'minimal')
 * @returns {JSX.Element} Accessible status bar component
 */
const StatusBar = ({
  imageCount,
  videoCount,
  totalSize,
  videoLimit = 3,
  variant = 'default', // 'default' or 'minimal'
}) => {
  const exceedsVideoLimit = videoCount > videoLimit;

  // Screen reader text for status summary
  const statusSummary = `${imageCount} images, ${videoCount} of ${videoLimit} videos allowed, total size ${formatFileSize(totalSize)}`;

  if (variant === 'minimal') {
    return (
      <div className="upload-limits mt-3" role="status" aria-label="Upload limits">
        <div className="d-flex justify-content-center gap-3 fs-6">
          <span className="upload-limit-item fs-6 d-flex align-items-center">
            <FiFilm className="me-1 text-danger icon-transparent size-small" aria-hidden="true" />
            <span id="video-limit-text" className="font-size-small">
              Max {videoLimit} videos
            </span>
          </span>
          <span className="upload-limit-item fs-6 d-flex align-items-center">
            <FiInfo className="me-1 text-primary icon-transparent size-small" aria-hidden="true" />
            <span id="size-limit-text" className="font-size-small">
              {formatFileSize(totalSize)} max
            </span>
          </span>
        </div>

        {/* Status summary for screen readers */}
        <div className="visually-hidden font-size-small" aria-live="polite">
          File limits: Maximum {videoLimit} videos and {formatFileSize(totalSize)} total size.
        </div>
      </div>
    );
  }

  return (
    <div
      className="d-flex justify-content-between align-items-center"
      role="region"
      aria-label="File upload status">
      <h2 className="m-0 fw-bold h5 font-size-medium">Media Uploader</h2>

      <div
        className="upload-stats d-flex gap-3 align-items-center fs-6"
        role="status"
        aria-live="polite">
        <span
          className="upload-stat-badge bg-light text-primary px-3 py-1 rounded-pill font-size-small"
          aria-label={`${imageCount} images`}>
          <FiImage className="me-1 icon-transparent size-small" aria-hidden="true" /> {imageCount}
        </span>

        <span
          className={`upload-stat-badge bg-light ${exceedsVideoLimit ? 'text-danger' : 'text-success'} px-3 py-1 rounded-pill font-size-small`}
          aria-label={`${videoCount} videos of ${videoLimit} maximum${exceedsVideoLimit ? ', limit exceeded' : ''}`}>
          <FiFilm className="me-1 icon-transparent size-small" aria-hidden="true" /> {videoCount}/
          {videoLimit}
        </span>

        <span
          className="upload-stat-badge bg-light text-secondary px-3 py-1 rounded-pill font-size-small"
          aria-label={`Total size: ${formatFileSize(totalSize)}`}>
          <FiInfo className="me-1 icon-transparent size-small" aria-hidden="true" />{' '}
          {formatFileSize(totalSize)}
        </span>
      </div>

      {/* Status summary for screen readers */}
      <div className="visually-hidden font-size-small">
        {statusSummary}
        {exceedsVideoLimit && ' Warning: Video limit exceeded.'}
      </div>
    </div>
  );
};

export default StatusBar;
